import React, { Fragment, useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import Form from '@lib/wrappers/Form';
import NamesInputs from './pageIncludes/common/names/NamesInputs';
import PageHeader from '../headers/PageHeader';
import RepresentativeAddressInputs from './pageIncludes/common/address/RepresentativeAddressInputs';
import UniversalInput from '@lib/wrappers/UniversalInput';

// ActionCreators

import { changeData, fetchCountiesForZipcode, setCounty } from '@actions/ssap/ssapActionCreators';
import { showModal } from '@actions/common/actionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { createSignature } from '@utils/householdMember/createSignature';
import { householdMemberTools } from '@utils/tools/householdMemberTools';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import validators from '@utils/validators/validators';

// Constants

import { authorizedRepresentativeTypes } from '@constants/ssap/hhmTypes/authorizedRepresentativeTypes.ts';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import EXTERNAL_LINKS from '@constants/configs/externalEndpoints';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { ssapModalTypes as MODAL_TYPES } from '@constants/ssap/modals/ssapModalTypes';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';




/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Application Assistance' Page
 *
 */
const Page1_03 = props => {

    const INITIAL_STATE = {
        signature: ''
    };

    const [localState, setLocalState] = useState(INITIAL_STATE);

    const {
        counties,
        data,
        data: {
            authorizedRepresentative,
            authorizedRepresentative: {
                hasUserElectronicallySigned,
                phone,
                partOfOrganizationIndicator,
                address: {
                    city,
                    county,
                    countyCode,
                    postalCode,
                    primaryAddressCountyFipsCode,
                    state,
                    streetAddress1,
                    streetAddress2
                },
                name: nameInAuthorizedRepresentative,
                emailAddress,
                organizationId,
                companyName
            },
            authorizedRepresentativeIndicator,
            getHelpIndicator,
            taxHousehold: {
                householdMember: householdMembers
            }
        },
        environment: {
            exchangeName,
            isState,
            stateCode
        },
        formName,
        pageName
    } = props;

    
    const {
        name
    } = householdMemberTools.getPrimaryContact(householdMembers);

    const isPostalCodeValid = validators
        .find(validator => validator.validatorType === VALIDATION_TYPES.ZIP_CODE)
        .verifier({ value: postalCode });

    useEffect(() => {
        if(state && isPostalCodeValid) {
            props.actions.fetchCountiesForZipcode(state, postalCode.slice(0, 5), pageName);
        }
        
    }, [ `${state}${postalCode}` ]);

    const fetchedCounties = (cc => cc ? cc : [])(counties[pageName]);

    useEffect(() => {
        if(fetchedCounties.length === 1) {

            const { countyCode } = fetchedCounties
                .reduce((res, c) => {
                    const [ countyCode ] = Object.entries(c)[0];
                    return {
                        ...res,
                        countyCode
                    };
                }, {});

            let newState = { ...data };
            newState = {
                ...newState,
                authorizedRepresentative: {
                    ...newState.authorizedRepresentative,
                    address: {
                        ...newState.authorizedRepresentative.address,
                        countyCode
                    }
                }
            };
            props.actions.changeData(newState);

        }
    }, [ fetchedCounties.map(c => Object.keys(c)).toString() ]);

    const syncUpCountyRelatedProps = () => {

        const [ updFips, updCounty ] =
            (c => c ? Object.entries(c)[0] : [ primaryAddressCountyFipsCode, county ] )(fetchedCounties.find(c => c[countyCode]));

        let newState = { ...data };

        newState = {
            ...newState,
            authorizedRepresentative: {
                ...newState.authorizedRepresentative,
                address: {
                    ...newState.authorizedRepresentative.address,
                    county: updCounty,
                    primaryAddressCountyFipsCode: updFips
                }
            }
        };

        props.actions.changeData(newState);
    };

    useEffect(() => {
        if(countyCode) {
            syncUpCountyRelatedProps();
        }
    }, [ countyCode]);

    useEffect(() => {
        if(!isPostalCodeValid) {
            fetchedCounties.length > 0 && props.actions.setCounty([], pageName);
            let newState = { ...data };
            newState = {
                ...newState,
                authorizedRepresentative: {
                    ...newState.authorizedRepresentative,
                    address: {
                        ...newState.authorizedRepresentative.address,
                        countyCode: null
                    }
                }
            };
            props.actions.changeData(newState);
        }
    }, [ postalCode ]);

    // Updating some properties in JSON

    const onFieldChange = (...args) => {

        let newState = { ...data };

        let { name, newData, value } = parseFieldChangeArgs(args);
        
        const updateSoreAndCleanState = (payload, shouldBeCleaned = undefined) => {
            newState = {
                ...newData,
                ...payload
            };
            shouldBeCleaned && setLocalState(INITIAL_STATE);
        };
        
        switch(true) {
            case name === 'getHelpIndicator' && (value === authorizedRepresentativeTypes.SELF || value === authorizedRepresentativeTypes.PROFESSIONAL_ASSISTANT):
                updateSoreAndCleanState(
                    {
                        authorizedRepresentativeIndicator: null,
                        authorizedRepresentative: cloneObject(EMPTY_OBJECTS.AUTHORIZED_REPRESENTATIVE)
                    },
                    true
                );
                break;
            case name === 'authorizedRepresentativeIndicator' && !value:
                updateSoreAndCleanState(
                    { authorizedRepresentative: cloneObject(EMPTY_OBJECTS.AUTHORIZED_REPRESENTATIVE) },
                    true
                );
                break;
            case name === 'partOfOrganizationIndicator' && !value:
                updateSoreAndCleanState({
                    authorizedRepresentative: {
                        ...newData.authorizedRepresentative,
                        'partOfOrganizationIndicator': value,
                        'organizationId': null,
                        'companyName': null
                    }
                });
                break;
        }

        props.actions.changeData(newState);
    };

    // Updating <address> object in <authorizedRepresentative> object with the simultaneous check if state is changed

    const onFieldChange_name_and_address = newData => {
        const currentState = newData.address.state;

        if (currentState !== authorizedRepresentative.address.state) {
            newData.address.countyCode = null;
        }

        let newState = { ...data };
        newState.authorizedRepresentative = newData;
        props.actions.changeData(newState);
    };

    // Updating exact type of phone extension in <phone> object

    const onFieldChange_phone_extension = (newData, indexInArray) => {
        let newState = { ...data };
        newState.authorizedRepresentative.phone[indexInArray] = newData;
        props.actions.changeData(newState);
    };

    // Updating <signature> value in LocalState

    const onFieldChange_signature = newData => {
        setLocalState(state => ({
            ...state,
            ...newData
        }));
    };

    const onFieldBlur_signing = () => {
        setLocalState(state => ({
            ...state,
            signature: state.signature.trim()
        }));
    };

    // Opening <Local Assistance Modal>

    const handleOpeningModal = e => {
        e.preventDefault();
        props.actions.showModal({
            modalType: MODAL_TYPES.LOCATE_ASSISTANCE_MODAL,
            modalData: {
                customEvent: 'close_LocateAssistanceModal',
                skipReloadOnDesignation: true
            }
        });
    };

    const handlePhoneChange = (...args) => {
        const [ newData , indexInArray ] = args;
        const newState = { ...data };

        newState
            .authorizedRepresentative
            .phone[indexInArray]
            .phoneNumber = newData.phoneNumber;
        props.actions.changeData(newState);
    };

    const brokerLink = (
        <p key='assistance_link' className="small-text margin-top-0 margin-bottom-1em">
            <FormattedMessage
                id="ssap.start.help_applying.assistance_link"
                values={{
                    link: (
                        <a
                            className="usa-external_link"
                            id="pop_getAssistance"
                            href="#"
                            onClick={handleOpeningModal}
                        >
                            <FormattedMessage id="ssap.start.help_applying.assistance_link_text"/>
                        </a>
                    )
                }}
            />
        </p>  
    );

    const {
        firstName,
        middleName,
        lastName,
        suffix
    } = nameInAuthorizedRepresentative || {};

    return (
        <Fragment>
            <div id="saApplyingForCoverage" className="">
                <Form  { ...{ formName } }>
                    <div className="subsection">
                        <PageHeader
                            text='ssap.subheader.who_is_helping_you'
                            headerClassName={'gi-heading-alt'}
                        />
                        <UniversalInput
                            legend={{
                                legendText: 'ssap.input.legend.any_helps'
                            }}
                            label={{
                                showRequiredIcon: true
                            }}
                            inputData={{
                                isInputRequired: true,
                                managingValues: {
                                    isBrokerSelected: (
                                        getHelpIndicator === authorizedRepresentativeTypes.PROFESSIONAL_ASSISTANT ||
                                        isState.NM
                                    )
                                },
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'getHelpIndicator',
                                value: getHelpIndicator,
                                options: DROPDOWN_OPTIONS.AUTHORIZED_REPRESENTATIVES,
                                validationType: VALIDATION_TYPES.SPECIFIED,
                                currentData: data,
                                fields: [],
                                optionsReplacements: {
                                    brokerLink
                                },
                                isInputStandalone: true
                            }}
                            errorClasses={['margin-l-0']}
                            inputActions={{
                                onFieldChange
                            }}
                        />

                    </div>
                    
                    {
                        getHelpIndicator === authorizedRepresentativeTypes.NON_PROFESSIONAL_ASSISTANT && 
                            
                        <div>
                            <div className="subsection fade-in">
                                <PageHeader
                                    text='ssap.subheader.authorized_representative'
                                    headerClassName={'gi-heading-alt'}
                                />
                                <FormattedMessage id="ssap.start.help_applying.designate_assistant" tagName="p"/>
                                <FormattedMessage id="ssap.start.help_applying.representative_description" values={{ br: <br/>, exchangeName }} tagName="p"/>
                                <div>
                                    <ul className="usa-list gi-list--ul">
                                        <FormattedMessage id="ssap.start.help_applying.item_one" tagName="li"/>
                                        <FormattedMessage id="ssap.start.help_applying.item_two" tagName="li" values={{ exchangeName }}/>
                                    </ul>
                                </div>
                                <FormattedMessage
                                    id="ssap.start.help_applying.note"
                                    tagName="p"
                                    values={{
                                        exchangeName: <strong>{ exchangeName }</strong>,
                                        strong: chunks => <strong>{chunks}</strong>,
                                        link: (
                                            <a
                                                className="usa-external_link"
                                                href={EXTERNAL_LINKS.NM_LINKS.BEWELL_APPT}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                <FormattedMessage id="common.here"/>
                                            </a>
                                        )
                                    }}
                                />
                            </div>
                            <div className="subsection">
                                <UniversalInput
                                    legend={{
                                        legendText: 'ssap.input.legend.name_your_representative',
                                    }}
                                    label={{
                                        labelClass: [''],
                                        ulClass:[''],
                                        showRequiredIcon: true
                                    }}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.RADIO_BUTTONS,
                                        name: 'authorizedRepresentativeIndicator',
                                        value: authorizedRepresentativeIndicator,
                                        options: DROPDOWN_OPTIONS.YES_NO,
                                        validationType: VALIDATION_TYPES.CHOSEN,
                                        currentData: data,
                                        fields: [],
                                        isInputStandalone: true
                                    }}
                                    inputActions={{
                                        onFieldChange
                                    }}
                                    errorClasses={['margin-l-0']}
                                />
                                {/************************************************************
                                            Begin - If User is #authorizedRepresentativeIndicator (Yes)
                                        *************************************************************/}
    
                                {
                                    authorizedRepresentativeIndicator &&
    
                                            <Fragment>
    
                                                <PageHeader
                                                    text='ssap.subheader.authorized_representative_contact_info'
                                                    headerClassName={'gi-heading-alt'}
                                                />
                                                <NamesInputs
                                                    {...{
                                                        firstName,
                                                        middleName,
                                                        lastName,
                                                        suffix,
                                                        stateCode,
                                                        currentData: authorizedRepresentative,
                                                        fields: ['name'],
                                                        isRequired: true,
                                                        inputActions: {
                                                            onFieldChange: onFieldChange_name_and_address
                                                        },
                                                        underFormControl: true,
                                                        isInputStandalone: true
                                                    }}
                                                />
                                                <UniversalInput
                                                    label={{
                                                        text: 'ssap.input.label.email'
                                                    }}
                                                    inputData={{
                                                        isInputRequired: true,
                                                        type: INPUT_TYPES.REGULAR_INPUT,
                                                        name: 'emailAddress',
                                                        helperClasses: [],
                                                        value: emailAddress,
                                                        placeholder: 'ssap.input.placeholder.email',
                                                        validationType: VALIDATION_TYPES.EMAIL,
                                                        currentData: data,
                                                        fields: ['authorizedRepresentative'],
                                                        isInputStandalone: true
                                                    }}
                                                    inputActions={{
                                                        onFieldChange
                                                    }}
                                                />
    
                                                <PageHeader
                                                    text='ssap.subheader.authorized_representative_home_address'
                                                    headerClassName={'gi-heading-alt margin-tb-20-10'}
                                                />
                                                <RepresentativeAddressInputs
                                                    {...{
                                                        postalCode,
                                                        countyCode,
                                                        streetAddress1,
                                                        streetAddress2,
                                                        state,
                                                        city,
                                                        counties: fetchedCounties,
                                                        currentData: authorizedRepresentative,
                                                        inputActions: {
                                                            onFieldChange: onFieldChange_name_and_address
                                                        },
                                                        underFormControl: true,
                                                        isInputStandalone: true
                                                    }}
                                                />
    
    
                                                <PageHeader
                                                    text='ssap.subheader.authorized_representative_phone'
                                                    headerClassName={'gi-heading-alt margin-tb-20-10'}
                                                />
    
                                                {
                                                    phone.map((ph, i) => {
    
                                                        const {
                                                            phoneExtension,
                                                            phoneNumber,
                                                            phoneType
                                                        } = ph;
    
                                                        const getPhoneType = type => {
                                                            switch(type) {
                                                                case 'WORK': return 'ssap.input.label.work_phone';
                                                                case 'CELL': return 'ssap.input.label.mobile_phone';
                                                                case 'HOME': return 'ssap.input.label.home_phone';
                                                            }
                                                        };
    
                                                        return (
                                                            <Fragment key={i}>
                                                                <UniversalInput
                                                                    label={{
                                                                        text: getPhoneType(phoneType),
                                                                        replacements: { alt: false }
                                                                    }}
                                                                    inputData={{
                                                                        isInputRequired: false,
                                                                        type: INPUT_TYPES.PHONE_NUMBER_INPUT,
                                                                        name: 'phoneNumber',
                                                                        helperClasses: [],
                                                                        value: phoneNumber,
                                                                        placeholder: 'ssap.input.placeholder.phone_number',
                                                                        validationType: VALIDATION_TYPES.PHONE_NUMBER,
                                                                        currentData: authorizedRepresentative.phone[i],
                                                                        fields: [],
                                                                        isInputStandalone: true
                                                                    }}
                                                                    inputActions={{
                                                                        onFieldChange: handlePhoneChange
                                                                    }}
                                                                    indexInArray={i}
                                                                />
                                                                <UniversalInput
                                                                    label={{
                                                                        text: 'ssap.input.label.phone_extension'
                                                                    }}
                                                                    inputData={{
                                                                        isInputRequired: false,
                                                                        type: INPUT_TYPES.REGULAR_INPUT,
                                                                        name: 'phoneExtension',
                                                                        helperClasses: [],
                                                                        value: phoneExtension,
                                                                        placeholder: 'ssap.input.placeholder.phone_extension',
                                                                        validationType: VALIDATION_TYPES.PHONE_EXTENSION,
                                                                        currentData: authorizedRepresentative.phone[i],
                                                                        fields: [],
                                                                        isInputStandalone: true
                                                                    }}
                                                                    inputActions={{
                                                                        onFieldChange: onFieldChange_phone_extension
                                                                    }}
                                                                    indexInArray={i}
                                                                />
                                                            </Fragment>
                                                        );
                                                    })
                                                }
    
                                                {!isState.MN &&
                                                    <UniversalInput
                                                        legend={{
                                                            legendText: 'ssap.input.legend.is_part_of_organization'
                                                        }}
                                                        label={{
                                                            labelClass: ['margin-tb-20-10'],
                                                            ulClass: [''],
                                                            showRequiredIcon: true
                                                        }}
                                                        inputData={{
                                                            isInputRequired: true,
                                                            type: INPUT_TYPES.RADIO_BUTTONS,
                                                            name: 'partOfOrganizationIndicator',
                                                            value: partOfOrganizationIndicator,
                                                            options: DROPDOWN_OPTIONS.YES_NO,
                                                            validationType: VALIDATION_TYPES.CHOSEN,
                                                            currentData: data,
                                                            fields: ['authorizedRepresentative'],
                                                            isInputStandalone: true
                                                        }}
                                                        inputActions={{
                                                            onFieldChange
                                                        }}
                                                    />
                                                }
    
                                                {/*****************************************************
                                                 Begin - If User is #partOfOrganizationIndicator (Yes)
                                                 ******************************************************/}
    
                                                {
                                                    partOfOrganizationIndicator && !isState.MN &&
    
                                                    <Fragment>
                                                        <UniversalInput
                                                            label={{
                                                                text: 'ssap.input.label.organization_name'
                                                            }}
                                                            inputData={{
                                                                isInputRequired: true,
                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                name: 'companyName',
                                                                helperClasses: [],
                                                                value: companyName,
                                                                placeholder: 'ssap.input.placeholder.organization_name',
                                                                currentData: data,
                                                                validationType: VALIDATION_TYPES.REPRESENTATIVE_ORGANIZATION_NAME,
                                                                fields: ['authorizedRepresentative'],
                                                                isInputStandalone: true
                                                            }}
                                                            inputActions={{
                                                                onFieldChange
                                                            }}
                                                        />
                                                        <UniversalInput
                                                            label={{
                                                                text: 'ssap.input.label.organization_id'
                                                            }}
                                                            inputData={{
                                                                isInputRequired: true,
                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                name: 'organizationId',
                                                                helperClasses: [],
                                                                value: organizationId,
                                                                placeholder: 'ssap.input.placeholder.organization_id',
                                                                currentData: data,
                                                                validationType: VALIDATION_TYPES.REPRESENTATIVE_ORGANIZATION_ID,
                                                                fields: ['authorizedRepresentative'],
                                                                isInputStandalone: true
                                                            }}
                                                            inputActions={{
                                                                onFieldChange
                                                            }}
                                                        />
                                                    </Fragment>
                                                }
    
                                                {/****************************************************
                                                 End - If User is #partOfOrganizationIndicator (Yes)
                                                 *****************************************************/}
    
                                                <UniversalInput
                                                    label={{
                                                        text: 'ssap.input.label.user_needs_to_sign',
                                                        labelTextReplacements: {
                                                            personName: normalizeNameOfPerson(name)
                                                        }
                                                    }}
                                                    inputData={{
                                                        isInputRequired: true,
                                                        type: INPUT_TYPES.CHECKBOX,
                                                        name: 'hasUserElectronicallySigned',
                                                        checked: hasUserElectronicallySigned,
                                                        validationType: VALIDATION_TYPES.CHECKED,
                                                        value: hasUserElectronicallySigned,
                                                        currentData: data,
                                                        fields: ['authorizedRepresentative'],
                                                        isInputStandalone: true
                                                    }}
                                                    inputActions={{
                                                        onFieldChange: onFieldChange
                                                    }}
                                                />
    
                                                {/**************************************************
                                                 Begin - If User is #signatureisProofIndicator (Yes)
                                                 ***************************************************/}
    
    
                                                <UniversalInput
                                                    label={{
                                                        text: 'ssap.input.label.type_full_name'
                                                    }}
                                                    inputData={{
                                                        isInputRequired: true,
                                                        type: INPUT_TYPES.REGULAR_INPUT,
                                                        name: 'signature',
                                                        helperClasses: [],
                                                        value: localState.signature,
                                                        valuesToMatch: createSignature(name),
                                                        placeholder: 'ssap.input.placeholder.signature',
                                                        validationType: VALIDATION_TYPES.IS_SIGNED,
                                                        currentData: localState,
                                                        fields: [],
                                                        isInputStandalone: true
                                                    }}
                                                    inputActions={{
                                                        onFieldChange: onFieldChange_signature,
                                                        onFieldBlur: onFieldBlur_signing
                                                    }}
                                                />
    
                                                {/*************************************************
                                                 End - If User is #signatureisProofIndicator (Yes)
                                                 **************************************************/}
    
                                            </Fragment>
                                }
    
                                {/************************************************************
                                         End - If User is #authorizedRepresentativeIndicator (Yes)
                                         *************************************************************/}
                            </div>
                        </div>
                            
                    }

                </Form>
            </div>
        </Fragment>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,

    counties: state.ssap.counties,
    data: state.ssap.data,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData,
        fetchCountiesForZipcode,
        setCounty,
        showModal
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page1_03);
